import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { TaskAiAnalysis } from "@recobra/schemas/task";
import { Field, FieldError, FieldLabel } from "@recobra/ui/field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@recobra/ui/select";

import { getErrorMessage } from "../../lib/form";
import { AiSuggestionStatus } from "./AiSuggestionStatus";

const AiFieldHeader: FC<{
  fieldLabel: string;
  isEditMode: boolean;
  aiReasoning?: string;
  aiValueLabel?: string;
  aiAnalysisStatus?: TaskAiAnalysis["status"];
}> = ({ fieldLabel, isEditMode, aiReasoning, aiValueLabel, aiAnalysisStatus }) => {
  const showsAiSuggestion = isEditMode || Boolean(aiValueLabel) || aiAnalysisStatus === "failed";

  return (
    <>
      <FieldLabel>
        {fieldLabel}
        {isEditMode && <span className="text-destructive">*</span>}
      </FieldLabel>
      {showsAiSuggestion ? (
        <AiSuggestionStatus
          value={aiValueLabel}
          reasoning={aiReasoning}
          status={aiAnalysisStatus}
          className="text-xs"
        />
      ) : null}
    </>
  );
};

const AiFieldControl: FC<{
  isEditMode: boolean;
  value: string;
  onChange: (val: string) => void;
  options: readonly string[];
  getLabel: (val: string) => string;
  valueError?: string;
}> = ({ isEditMode, value, onChange, options, getLabel, valueError }) =>
  isEditMode ? (
    <Select value={value} onValueChange={(val) => onChange(val ?? "")} itemToStringLabel={getLabel}>
      <SelectTrigger className="w-full" aria-invalid={Boolean(valueError)}>
        <SelectValue placeholder={m.clients_select_placeholder()} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>
            {getLabel(opt)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : (
    <div className="text-sm font-semibold text-foreground">
      {value ? getLabel(value) : m.common_em_dash()}
    </div>
  );

export const AiReviewField: FC<{
  fieldLabel: string;
  options: readonly string[];
  getLabel: (val: string) => string;
  isEditMode: boolean;
  value: string;
  onChange: (val: string) => void;
  aiReasoning?: string;
  aiValueLabel?: string;
  aiAnalysisStatus?: TaskAiAnalysis["status"];
  valueErrors?: ReadonlyArray<unknown>;
}> = ({
  fieldLabel,
  options,
  getLabel,
  isEditMode,
  value,
  onChange,
  aiReasoning,
  aiValueLabel,
  aiAnalysisStatus,
  valueErrors = [],
}) => {
  const valueError = getErrorMessage(valueErrors[0]);

  return (
    <Field data-invalid={valueError ? "true" : undefined}>
      <AiFieldHeader
        fieldLabel={fieldLabel}
        isEditMode={isEditMode}
        aiReasoning={aiReasoning}
        aiValueLabel={aiValueLabel}
        aiAnalysisStatus={aiAnalysisStatus}
      />
      <AiFieldControl
        isEditMode={isEditMode}
        value={value}
        onChange={onChange}
        options={options}
        getLabel={getLabel}
        valueError={valueError}
      />
      {valueError ? <FieldError>{valueError}</FieldError> : null}
    </Field>
  );
};
